import type { AuthenticationContext } from '@pflegenavi/frontend/authentication';
import type { Tenant } from '@pflegenavi/frontend/tenant';
import type { Api } from '@pflegenavi/shared-frontend/platform';
import {
  defaultHeaders,
  get,
  getPhoenixApiTenantUrl,
  modify,
  superFetch,
} from '@pflegenavi/shared-frontend/platform';
import type { ReceiptState, ReceiptStatusValues } from '@pflegenavi/shared/api';
import { Filter } from '../filter';

interface ServiceProvider {
  id: string;
  name: string;
  global_service_provider_id: string;
}

interface ReceiptType {
  id: string;
  name: string;
  requiresServiceProvider: boolean;
}

interface Resident {
  id: string;
  first_name: string;
  last_name: string;
  gender: string;
}

interface Employee {
  id: string;
  first_name: string;
  last_name: string;
}

interface ReceiptBatchEntry {
  id: string;
  receipt_batch_id: string;
}

interface ReceiptCancellation {
  transaction_id: string;
  cancel_transaction_id: string;
  employee: Employee | null;
  service_provider: ServiceProvider | null;
  note: string;
  date: Date;
}

interface Transaction {
  id: string;
  type: string;
  source_type: string;
  creator_employee: Employee | null;
  inserted_at: Date;
}

export interface ReceiptHistoryEntry {
  history_entry_id: string;
  action_timestamp: string;
  receipt_action: 'INSERT' | 'UPDATE' | 'DELETE';
  receipt_state: ReceiptState;
  updated_by_employee: Employee | null;
}

interface CashTransactionGroupList {
  id: string;
  cash_list_id: string;
  cash_changed: number | null;
  semantic_creation_date: string | null;
}

interface CashListTransactionGroupLink {
  cash_list_transaction_group_id: string;
  cash_transaction_group_list: CashTransactionGroupList | null;
}

export interface ReceiptPhoenix {
  id: string;
  notes: string | null;
  service_provider: ServiceProvider;
  receipt_type: ReceiptType;
  resident: Resident | null;
  amount: number;
  additional_fee_amount: number;
  included_fee_amount: number;
  additional_fees_diverted_to_pflegenavi: boolean;
  receipt_date: Date;
  receipt_image_ids: string[];
  receipt_not_available: boolean;
  submission_date: Date | null;
  embargo_date: Date | null;
  submitted_by_employee: Employee | null;
  created_by_employee: Employee | null;
  receipt_state: ReceiptState;
  receipt_batch_entry: ReceiptBatchEntry | null;
  transactions: Transaction[];
  cancellation: ReceiptCancellation | null;
  settlement_date: Date | null;
  settlement_amount: number | null;
  settlement_notes: string | null;
  settled_by_employee: Employee | null;
  settlement_cash_list_transaction_group_link: CashListTransactionGroupLink | null;
  cash_list_transaction_group_link: CashListTransactionGroupLink | null;
  inserted_at: Date;
  earnings: boolean;
  history: ReceiptHistoryEntry[] | null;
}

export interface StatusTotals {
  Draft: number;
  Incomplete: number;
  Pending: number;
  Submitted: number;
  AwaitingSettlement: number;
  Settled: number;
  All: number;
}

export interface ReceiptIndexResponse {
  data: ReceiptPhoenix[];
  meta: {
    page_size: number;
    current_page: number;
    total_pages: number;
    total_count: number;
  };
  status_totals: StatusTotals;
}
export interface GetReceiptsParams {
  page: number;
  page_size: number;
  nursing_home_ids?: string[];
  service_provider_ids?: string[];
  created_by_employee_ids?: string[];
  resident_ids?: string[];
  receipt_states?: ReceiptStatusValues[];
  order_directions?: Array<'asc' | 'desc'>;
  order_by?: ReceiptPhoenixOrderableFields[];
  receipt_date_range?: [Date | undefined, Date | undefined];
  submission_date_range?: [Date | undefined, Date | undefined];
  cash_list_transaction_group_ids?: string[];
}

export type ReceiptPhoenixOrderableFields =
  | 'submission_date'
  | 'confirmation_date' // submission_date if it is set, otherwise settlement_date
  | 'receipt_date'
  | 'amount'
  | 'additional_fee_amount'
  | 'resident_name'
  | 'inserted_at';

export interface ReceiptBatchPhoenix {
  id: string;
  amount: number;
  receipt_count: number;
  receipt_date: Date;
  submission_date: Date | null;
  service_provider: ServiceProvider | null;
  receipt_type: ReceiptType | null;
  state: string;
  created_by_employee: Employee | null;
  inserted_at: Date;
}

export interface ReceiptOrBatchPhoenix {
  id: string;
  type: 'receipt' | 'receipt-batch';
  receiptState: ReceiptStatusValues | null;
  amountInCents: number;
  additionalFeeAmountInCents?: number;
  includedFeeAmountInCents?: number;
  receiptDate: Date;
  submittedAt: Date | null;
  createdAt: Date;
  serviceProvider: {
    id: string;
    name: string;
  } | null;
  receiptType: {
    id: string;
    name: string;
  } | null;
  resident: {
    id: string;
    firstName: string;
    lastName: string;
    gender: string;
  } | null;
  createdByEmployeeId: string;
  amountReceiptBatchInCents: number;
  amountReceiptsInCents: number;
  numberOfReceipts: number;
  minReceiptDate: Date | null;
  maxReceiptDate: Date | null;
  useIndividualDates: boolean;
  isCancelled: boolean | null;
  cancellationDate: Date | null;
}

export interface ReceiptsAndReceiptBatchesResponse {
  meta: {
    receiptCount: number;
    receiptBatchCount: number;
    totalCount: number;
    totalPages: number;
    pageSize: number;
    currentPage: number;
  };
  data: ReceiptOrBatchPhoenix[];
}

export interface ReceiptsAndReceiptBatchesParams {
  page: number;
  page_size: number;
  state?: string;
  sort?: 'receiptDate' | 'submittedAt' | 'createdOn';
  sortOrder?: 'asc' | 'desc';
  submissionDate?: {
    lt?: Date;
    gt?: Date;
    eq?: Date;
    gte?: Date;
    lte?: Date;
  };
  createdByEmployeeIds?: string[];
  nursingHomeId: string;
  cashTransactionGroupId?: string;
}

export interface CreateReceiptPhoenixParams {
  resident_id: string;
  amount: number; // In cents
  receipt_type_id?: string;
  service_provider?: string;
  service_provider_id?: string;
  receipt_date: Date; // ISO date string
  notes?: string;
  receipt_image_ids?: string[];
  receipt_not_available?: boolean;
  additional_fee_amount?: number;
  included_fee_amount?: number;
  cash_list_transaction_group_link?: {
    cash_list_transaction_group_id: string;
  };
}

export interface IReceiptPhoenixApi extends Api {
  create(dataIn: { body: CreateReceiptPhoenixParams }): Promise<{
    data: ReceiptPhoenix;
  }>;
  submit(dataIn: {
    body: { receipt_ids: string[] };
    query?: { use_actually_available_balance?: boolean };
  }): Promise<{
    data: Array<{
      id: string;
      error?: number;
      message?: string;
    }>;
  }>;
  cancel(dataIn: {
    body: { data: { note: string } };
    params: { receipt_id: string };
  }): Promise<void>;
  get(dataIn: {
    params: { receipt_id: string };
    query?: {
      preloads?: {
        cash_list_transaction_group_link?: boolean;
        receipt_batch_entry?: boolean;
      };
    };
  }): Promise<{ data: ReceiptPhoenix }>;
  index(dataIn: { params: GetReceiptsParams }): Promise<ReceiptIndexResponse>;
  getReceiptsAndReceiptBatches(dataIn: {
    params: ReceiptsAndReceiptBatchesParams;
  }): Promise<ReceiptsAndReceiptBatchesResponse>;
  transitionToAwaitingSettlement(dataIn: {
    params: { receipt_id: string };
  }): Promise<{ data: ReceiptPhoenix }>;
  revertAwaitingSettlement(dataIn: {
    params: { receipt_id: string };
  }): Promise<{ data: ReceiptPhoenix }>;
  settle(dataIn: {
    params: { receipt_id: string };
    body: {
      cash_transaction_group_id?: string;
      notes?: string;
      settlement_date?: Date;
    };
  }): Promise<{ data: ReceiptPhoenix }>;
  getFamilyMemberReceipts(dataIn: {
    params: { tenant: string; residentId: string };
  }): Promise<ReceiptIndexResponse>;
}

export class ReceiptPhoenixApi implements IReceiptPhoenixApi {
  baseUrl: string;
  familyMemberBaseUrl: string;

  create: IReceiptPhoenixApi['create'];
  submit: IReceiptPhoenixApi['submit'];
  cancel: IReceiptPhoenixApi['cancel'];
  get: IReceiptPhoenixApi['get'];
  index: IReceiptPhoenixApi['index'];
  getReceiptsAndReceiptBatches: IReceiptPhoenixApi['getReceiptsAndReceiptBatches'];
  transitionToAwaitingSettlement: IReceiptPhoenixApi['transitionToAwaitingSettlement'];
  revertAwaitingSettlement: IReceiptPhoenixApi['revertAwaitingSettlement'];
  settle: IReceiptPhoenixApi['settle'];
  getFamilyMemberReceipts: IReceiptPhoenixApi['getFamilyMemberReceipts'];

  constructor(
    tenantId: Tenant,
    public authContext: AuthenticationContext,
    apiUrl?: string
  ) {
    this.baseUrl = getPhoenixApiTenantUrl(tenantId, apiUrl);
    this.familyMemberBaseUrl = getPhoenixApiTenantUrl('family-member', apiUrl);

    this.create = modify({
      url: `${this.baseUrl}/receipt`,
      authContext,
      method: 'POST',
      headers: defaultHeaders(),
    });

    this.submit = async (dataIn: {
      body: { receipt_ids: string[] };
      query?: { use_actually_available_balance?: boolean };
    }) => {
      const url = dataIn.query?.use_actually_available_balance
        ? `${this.baseUrl}/receipt/submit?use_actually_available_balance=true`
        : `${this.baseUrl}/receipt/submit`;

      const result = await superFetch(authContext, url, {
        method: 'POST',
        headers: defaultHeaders(),
        body: JSON.stringify(dataIn.body),
      });

      const data = await result.json();
      if (data && data.statusCode >= 400) {
        throw new Error(data.message ?? 'Unknown error');
      }

      return data;
    };

    this.cancel = modify({
      url: (params) => `${this.baseUrl}/receipt/${params.receipt_id}/cancel`,
      authContext,
      method: 'POST',
      headers: defaultHeaders(),
    });

    this.get = async (dataIn: {
      params: { receipt_id: string };
      query?: {
        preloads?: {
          cash_list_transaction_group_link?: boolean;
          receipt_batch_entry?: boolean;
        };
      };
    }): Promise<{ data: ReceiptPhoenix }> => {
      const queryParameters = new URLSearchParams();

      if (dataIn.query?.preloads?.cash_list_transaction_group_link) {
        queryParameters.append(
          'preloads[cash_list_transaction_group_link]',
          'true'
        );
      }

      if (dataIn.query?.preloads?.receipt_batch_entry) {
        queryParameters.append('preloads[receipt_batch_entry]', 'true');
      }

      const queryString = queryParameters.toString();
      const url = `${this.baseUrl}/receipt/${dataIn.params.receipt_id}${
        queryString ? `?${queryString}` : ''
      }`;

      const result = await superFetch(authContext, url, {
        method: 'GET',
        headers: defaultHeaders(),
      });

      const data = await result.json();
      if (data && data.statusCode >= 400) {
        throw new Error(data.message ?? 'Unknown error');
      }
      return data;
    };

    this.index = get({
      url: (params) => {
        const filter = new Filter();

        // `if (x)` on these array params (no length check) is intentional:
        // an explicit `[]` still sends the (valueless) `in` filter, matching
        // zero rows, rather than being treated as absent like
        // `fieldIfPresent` would.
        if (params.nursing_home_ids) {
          filter.field('nursing_home_id', 'in', params.nursing_home_ids);
        }
        if (params.service_provider_ids) {
          filter.field(
            'service_provider_id',
            'in',
            params.service_provider_ids
          );
        }
        if (params.created_by_employee_ids) {
          filter.field(
            'created_by_employee_id',
            'in',
            params.created_by_employee_ids
          );
        }
        if (params.resident_ids) {
          filter.field('resident_id', 'in', params.resident_ids);
        }
        if (params.receipt_states) {
          filter.field('receipt_state', 'in', params.receipt_states);
        }

        filter.fieldIfPresent(
          'receipt_date',
          '>=',
          params.receipt_date_range?.[0]?.toISOString()
        );
        filter.fieldIfPresent(
          'receipt_date',
          '<=',
          params.receipt_date_range?.[1]?.toISOString()
        );
        filter.fieldIfPresent(
          'submission_date',
          '>=',
          params.submission_date_range?.[0]?.toISOString()
        );
        filter.fieldIfPresent(
          'submission_date',
          '<=',
          params.submission_date_range?.[1]?.toISOString()
        );
        if (params.cash_list_transaction_group_ids) {
          filter.field(
            'cash_list_transaction_group_id',
            'in',
            params.cash_list_transaction_group_ids
          );
        }

        const query = filter.build();

        query.append('page', params.page.toString());

        query.append('page_size', params.page_size.toString());

        if (params.order_by) {
          for (const orderBy of params.order_by) {
            query.append('order_by[]', orderBy);
          }
        }

        if (params.order_directions) {
          for (const orderDirection of params.order_directions) {
            query.append('order_directions[]', orderDirection);
          }
        }

        return `${this.baseUrl}/receipt?${query.toString()}`;
      },
      authContext,
      headers: defaultHeaders(),
    });

    this.getReceiptsAndReceiptBatches = get({
      url: (params) => {
        const query = new URLSearchParams();

        query.append('nursingHomeId', params.nursingHomeId);

        if (params.page) {
          query.append('page', params.page.toString());
        }

        if (params.page_size) {
          query.append('page_size', params.page_size.toString());
        }

        if (params.state) {
          query.append('state', params.state);
        }

        if (params.sort) {
          query.append('sort', params.sort);
        }

        if (params.sortOrder) {
          query.append('sortOrder', params.sortOrder);
        }

        if (params.createdByEmployeeIds) {
          for (const employeeId of params.createdByEmployeeIds) {
            query.append('createdByEmployeeIds[]', employeeId);
          }
        }

        if (params.submissionDate) {
          const fields = ['lt', 'gt', 'eq', 'gte', 'lte'] as const;
          fields.forEach((field) => {
            const dateField = params.submissionDate?.[field];
            if (dateField) {
              query.append(`submissionDate[${field}]`, dateField.toISOString());
            }
          });
        }

        if (params.cashTransactionGroupId) {
          query.append('cashTransactionGroupId', params.cashTransactionGroupId);
        }

        const queryString = query.toString();
        return `${this.baseUrl}/receipts-and-receipt-batches?${queryString}`;
      },
      authContext,
      headers: defaultHeaders(),
    });

    this.transitionToAwaitingSettlement = modify({
      url: (params) =>
        `${this.baseUrl}/receipt/${params.receipt_id}/transition_to_awaiting_settlement`,
      authContext,
      method: 'POST',
      headers: defaultHeaders(),
    });

    this.revertAwaitingSettlement = modify({
      url: (params) =>
        `${this.baseUrl}/receipt/${params.receipt_id}/revert_awaiting_settlement`,
      authContext,
      method: 'POST',
      headers: defaultHeaders(),
    });

    this.settle = modify({
      url: (params) => `${this.baseUrl}/receipt/${params.receipt_id}/settle`,
      authContext,
      method: 'POST',
      headers: defaultHeaders(),
    });

    this.getFamilyMemberReceipts = get({
      url: (params) =>
        `${this.familyMemberBaseUrl}/${params.tenant}/${params.residentId}/receipts`,
      authContext,
      headers: defaultHeaders(),
    });
  }
}
